import { useEffect, useMemo, useState } from 'react';
import {
  fetchSessionHistory,
  fetchSessions,
  fetchUsage,
  sumUsage,
  type SessionSummary,
  type SessionTurn,
  type InstanceUsage,
} from '../api/sessions';
import './SessionsPage.css';

function formatRelativeTime(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'in the future';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

function formatTokens(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function shortId(id: string): string {
  return id ? id.slice(0, 8) : '—';
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [usage, setUsage] = useState<InstanceUsage[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [history, setHistory] = useState<SessionTurn[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadList = () => {
    setLoadingList(true);
    setError(null);
    Promise.all([fetchSessions(), fetchUsage().catch(() => null)])
      .then(([list, u]) => {
        setSessions(list);
        setUsage(u);
        const first = list[0];
        if (first && !selectedId) {
          setSelectedId(first.session_id);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    fetchSessionHistory(selectedId)
      .then(setHistory)
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoadingHistory(false));
  }, [selectedId]);

  const sessionTokenTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const turn of history) {
      if (turn.token_count) {
        totals.set('total', (totals.get('total') ?? 0) + turn.token_count);
      }
    }
    return totals;
  }, [history]);

  const compactedCount = useMemo(
    () => history.filter((t) => Boolean(t.compacted)).length,
    [history],
  );

  return (
    <div className="sessions-page">
      <header className="sessions-page__header">
        <div>
          <div className="sessions-page__title">Sessions</div>
          <div className="sessions-page__subtitle">
            {usage && usage.length > 0
              ? `${formatTokens(sumUsage(usage, 'total_input_tokens'))} in · ${formatTokens(sumUsage(usage, 'total_output_tokens'))} out · ${sumUsage(usage, 'total_turns')} turns across ${usage.length} instance${usage.length === 1 ? '' : 's'}`
              : sessions.length > 0
                ? `${sessions.length} session${sessions.length === 1 ? '' : 's'}`
                : '—'}
          </div>
        </div>
        <button
          type="button"
          className="sessions-page__refresh"
          onClick={loadList}
          disabled={loadingList}
        >
          {loadingList ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      {error && <div className="sessions-page__error">{error}</div>}

      <div className="sessions-page__split">
        <aside className="sessions-page__list">
          {loadingList && sessions.length === 0 && (
            <div className="sessions-page__status">Loading...</div>
          )}
          {!loadingList && sessions.length === 0 && (
            <div className="sessions-page__status">No sessions yet.</div>
          )}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              type="button"
              className={
                'sessions-page__row' +
                (s.session_id === selectedId
                  ? ' sessions-page__row--active'
                  : '')
              }
              onClick={() => setSelectedId(s.session_id)}
            >
              <div className="sessions-page__row-id">{shortId(s.session_id)}</div>
              <div className="sessions-page__row-meta">
                <span>{s.channel_id ?? 'unknown'}</span>
                <span>·</span>
                <span>{s.turn_count} turns</span>
                {s.active_turn_count !== s.turn_count && (
                  <>
                    <span>·</span>
                    <span title="active (non-compacted)">
                      {s.active_turn_count} active
                    </span>
                  </>
                )}
              </div>
              <div className="sessions-page__row-time">
                {formatRelativeTime(s.last_active_ms)}
              </div>
            </button>
          ))}
        </aside>

        <section className="sessions-page__detail">
          {!selectedId && (
            <div className="sessions-page__status">Select a session.</div>
          )}
          {selectedId && (
            <>
              <div className="sessions-page__detail-head">
                <div className="sessions-page__detail-title">
                  {shortId(selectedId)}
                  <span className="sessions-page__detail-full">
                    {selectedId}
                  </span>
                </div>
                <div className="sessions-page__detail-meta">
                  {history.length} turn{history.length === 1 ? '' : 's'}
                  {compactedCount > 0 && (
                    <> · {compactedCount} compacted</>
                  )}
                  {sessionTokenTotals.has('total') && (
                    <> · {formatTokens(sessionTokenTotals.get('total')!)} tokens</>
                  )}
                </div>
              </div>
              {loadingHistory && history.length === 0 && (
                <div className="sessions-page__status">Loading turns...</div>
              )}
              <div className="sessions-page__turns">
                {history.map((turn) => (
                  <TurnCard key={turn.turn_id} turn={turn} />
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function TurnCard({ turn }: { turn: SessionTurn }) {
  const compacted = Boolean(turn.compacted);
  const roleClass = `sessions-page__turn--${turn.role.toLowerCase()}`;
  return (
    <div
      className={
        'sessions-page__turn ' +
        roleClass +
        (compacted ? ' sessions-page__turn--compacted' : '')
      }
    >
      <div className="sessions-page__turn-head">
        <span className="sessions-page__turn-role">{turn.role}</span>
        {turn.token_count !== null && turn.token_count > 0 && (
          <span className="sessions-page__turn-tokens">
            {formatTokens(turn.token_count)} tok
          </span>
        )}
        {compacted && (
          <span className="sessions-page__turn-flag">compacted</span>
        )}
        <span className="sessions-page__turn-time">
          {formatRelativeTime(turn.timestamp_ms)}
        </span>
      </div>
      {turn.content && (
        <pre className="sessions-page__turn-content">{turn.content}</pre>
      )}
      {turn.tool_calls && turn.tool_calls.length > 0 && (
        <div className="sessions-page__turn-tools">
          {turn.tool_calls.map((tc, i) => (
            <div key={tc.id ?? i} className="sessions-page__turn-tool">
              <span className="sessions-page__turn-tool-name">→ {tc.name}</span>
              <code className="sessions-page__turn-tool-args">
                {tc.arguments
                  ? JSON.stringify(tc.arguments).slice(0, 600)
                  : '(no args)'}
              </code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
